import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/contexts/TranslationContext';

const STRAY_PROBLEM_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/7e8l7lvf_Stray%20Pic%202.jpeg';
const DEVICE_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/4ye87oem_SmartPaw%20Feeder%20Drawing.jpeg';

const CombinedProblemSolution = () => {
  const { t } = useTranslation();
  
  return (
    <div className="py-20 md:py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top Section - Problem */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1C19] tracking-tight mb-8">
              {t('problem.title')}
            </h2>

            {/* Big Number */}
            <div className="mb-8">
              <div className="text-8xl md:text-9xl font-bold text-[#F4A460] mb-2">
                {t('problem.number')}
              </div>
              <p className="font-['Outfit'] text-3xl md:text-4xl text-[#1A1C19] font-medium">
                {t('problem.subtitle')}
              </p>
            </div>

            {/* Emotional Quote */}
            <div className="mb-8 pl-6 border-l-4 border-[#F4A460]">
              <p className="font-['Manrope'] text-lg text-[#5E635C] leading-relaxed mb-4">
                {t('problem.description1')}
              </p>
            </div>

            {/* Supporting Text */}
            <p className="font-['Manrope'] text-lg text-[#5E635C] leading-relaxed mb-4">
              {t('problem.description2')}
            </p>

            <p className="font-['Manrope'] text-lg text-[#1A1C19] font-medium leading-relaxed">
              {t('problem.description3')}
            </p>
          </motion.div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
              <img
                src={STRAY_PROBLEM_IMAGE}
                alt="Stray dog on the streets of Tbilisi"
                className="w-full h-auto"
              />
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="flex items-center justify-center my-16">
          <div className="h-px bg-gradient-to-r from-transparent via-[#EAEBEA] to-transparent w-full max-w-3xl"></div>
        </div>

        {/* Bottom Section - Solution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left - Device Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.08)] bg-gradient-to-br from-[#FDF0E6] to-[#EBF3FC] p-8">
              <img
                src={DEVICE_IMAGE}
                alt="SmartPaw Feeder - Inside the Innovation"
                className="w-full h-auto"
              />
            </div>
          </motion.div>

          {/* Right - Solution Content + Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-5xl md:text-6xl font-bold text-[#1A1C19] tracking-tight mb-6">
              {t('solution.title')}
            </h2>
            
            <p className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#F4A460] italic mb-8">
              "{t('solution.quote')}"
            </p>

            <p className="font-['Manrope'] text-lg text-[#1A1C19] font-medium leading-relaxed mb-8">
              {t('solution.description1')}
            </p>

            <p className="font-['Manrope'] text-[#5E635C] leading-relaxed mb-12">
              {t('solution.description2')}
            </p>

            {/* Key Benefits - Moved here from bottom */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4A460]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#F4A460]" fill="none" strokeWidth="2" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-['Outfit'] font-semibold text-lg text-[#1A1C19] mb-2">{t('solution.benefits.health')}</h4>
                    <p className="font-['Manrope'] text-[#5E635C] text-sm leading-relaxed">{t('solution.benefits.healthDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#4A90E2]" fill="none" strokeWidth="2" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-['Outfit'] font-semibold text-lg text-[#1A1C19] mb-2">{t('solution.benefits.environmental')}</h4>
                    <p className="font-['Manrope'] text-[#5E635C] text-sm leading-relaxed">{t('solution.benefits.environmentalDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4A460]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#F4A460]" fill="none" strokeWidth="2" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-['Outfit'] font-semibold text-lg text-[#1A1C19] mb-2">{t('solution.benefits.sanitary')}</h4>
                    <p className="font-['Manrope'] text-[#5E635C] text-sm leading-relaxed">{t('solution.benefits.sanitaryDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CombinedProblemSolution;
